import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  Tabs,
  Tab,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArchiveOutlined as ArchiveIcon,
  Autorenew as RetryIcon,
  Close as CloseIcon,
  Campaign as CampaignIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  ErrorOutline as ErrorOutlineIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  VolumeUp as VolumeUpIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Flag as FlagIcon,
  SmartToy as SmartToyIcon,
  Analytics as AnalyticsIcon,
  Send as SendIcon,
  UnarchiveOutlined as UnarchiveIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

import conversationService, {
  ConversationArchiveStatus,
  ConversationDetail as ConversationDetailType,
  Message,
} from '../../services/conversationService';
import AudioPlayer from './AudioPlayer';
import ConversationAnalytics from './ConversationAnalytics';
import { websocketService } from '../../services/websocketService';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMeta } from '../../contexts/AppMetaContext';

interface ConversationDetailProps {
  conversationId: string;
  archiveStatus: ConversationArchiveStatus;
  onBack: () => void;
  onArchiveStateChange: (isArchived: boolean) => void;
}

const chatColors = {
  canvas: '#efeae2',
  canvasTint: 'rgba(255,255,255,0.34)',
  canvasShadow: 'rgba(17, 27, 33, 0.03)',
  frame: '#f0f2f5',
  border: '#d1d7db',
  outgoing: '#d9fdd3',
  proactive: '#cffce0',
  proactiveAccent: '#087f73',
  incoming: '#ffffff',
  text: '#111b21',
  meta: '#667781',
  accent: '#00a884',
  accentDark: '#008f72',
  systemNotice: 'rgba(255, 255, 255, 0.8)',
};

const getDayKey = (timestamp: string): string => format(parseISO(timestamp), 'yyyy-MM-dd');

const getDayLabel = (timestamp: string): string =>
  format(parseISO(timestamp), "EEEE, d 'de' MMMM", { locale: es });

const isIncomingMessage = (message: Message): boolean => message.sender === 'user';
const isRetryableManualFailure = (message: Message): boolean =>
  Boolean(message.is_human_takeover) &&
  Boolean(message.wa_status) &&
  ['failed', 'retry_exhausted'].includes(String(message.wa_status));

const humanizeErrorReason = (message: Message): string => {
  switch (message.wa_error_code) {
    case 131049:
      return 'Buzón del cliente saturado';
    case 131026:
      return 'Número inactivo o bloqueado';
    case 131050:
      return 'Cliente rechazó recibir marketing';
    case 130429:
      return 'Límite de envíos alcanzado';
    case 130472:
      return 'Restricción temporal de WhatsApp';
    default:
      return message.wa_error_description || 'No se pudo entregar';
  }
};

const getDeliveryIndicator = (message: Message) => {
  const status = message.wa_status;
  if (!status || isIncomingMessage(message)) {
    return null;
  }

  if (status === 'processing') {
    return {
      icon: <ScheduleIcon sx={{ fontSize: 15 }} />,
      color: '#667781',
      label: 'Procesando envío',
    };
  }
  if (status === 'sent') {
    return {
      icon: <DoneIcon sx={{ fontSize: 15 }} />,
      color: '#667781',
      label: 'Enviado',
    };
  }
  if (status === 'delivered') {
    return {
      icon: <DoneAllIcon sx={{ fontSize: 15 }} />,
      color: '#667781',
      label: 'Recibido por el cliente',
    };
  }
  if (status === 'read') {
    return {
      icon: <DoneAllIcon sx={{ fontSize: 15 }} />,
      color: '#0b57d0',
      label: 'Leído por el cliente',
    };
  }
  if (status === 'retry_scheduled') {
    const retryText = message.max_retries
      ? ` (${message.retry_count || 0}/${message.max_retries})`
      : '';
    return {
      icon: <RetryIcon sx={{ fontSize: 15 }} />,
      color: '#b26a00',
      label: `Reintento programado${retryText}`,
    };
  }
  if (status === 'retry_exhausted') {
    return {
      icon: <ErrorOutlineIcon sx={{ fontSize: 15 }} />,
      color: '#b3261e',
      label: 'Reintentos agotados',
    };
  }
  if (status === 'failed') {
    return {
      icon: <ErrorOutlineIcon sx={{ fontSize: 15 }} />,
      color: '#b3261e',
      label: humanizeErrorReason(message),
    };
  }

  return null;
};

type ProfileSnapshotEntry = {
  key: string;
  label: string;
  value: string;
  confidence?: number;
  source?: string;
  updatedAt?: string;
};

type ProfileSnapshotGroup = {
  title: string;
  entries: ProfileSnapshotEntry[];
};

const profileGroups = [
  {
    title: 'Perfil conversacional',
    fields: [
      'name',
      'location_interest',
      'budget_min',
      'budget_max',
      'property_type',
      'bedrooms',
      'timeline',
      'motivation',
      'contact_preference',
    ],
  },
  {
    title: 'HubSpot contacto',
    fields: [
      'tiene_mascotas',
      'gustos_y_preferencias',
      'ingresos_mensuales',
    ],
  },
  {
    title: 'Perfil HubSpot',
    fields: [
      'nivel_de_potencial',
      'tipo_de_inmueble',
      'presupuesto_estimado',
      'uso_del_inmueble',
      'rango_de_areas_que_busca',
      'que_le_gusto_del_proyecto_',
    ],
  },
  {
    title: 'Campos legacy',
    fields: [
      'ocupacion',
      'profesion_cliente',
      'rango_de_edad',
    ],
  },
];

const profileFieldLabels: Record<string, string> = {
  name: 'Nombre',
  location_interest: 'Ubicación de interés',
  budget_min: 'Presupuesto mínimo',
  budget_max: 'Presupuesto máximo',
  property_type: 'Tipo de inmueble anterior',
  bedrooms: 'Habitaciones',
  timeline: 'Tiempo de compra',
  motivation: 'Motivación anterior',
  contact_preference: 'Preferencia de contacto',
  tiene_mascotas: 'Tiene mascotas',
  gustos_y_preferencias: 'Gustos y preferencias',
  ingresos_mensuales: 'Ingresos mensuales',
  nivel_de_potencial: 'Nivel de potencial',
  tipo_de_inmueble: 'Tipo de inmueble',
  presupuesto_estimado: 'Presupuesto estimado',
  uso_del_inmueble: 'Uso del inmueble',
  rango_de_areas_que_busca: 'Rango de áreas que busca',
  que_le_gusto_del_proyecto_: 'Qué le gustó del proyecto',
  ocupacion: 'Ocupación',
  profesion_cliente: 'Profesión del cliente',
  rango_de_edad: 'Rango de edad',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

const humanizeProfileKey = (key: string): string =>
  profileFieldLabels[key] ||
  key
    .replace(/_+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();

const formatProfileValue = (value: unknown): string => {
  if (!hasMeaningfulValue(value)) return 'Sin dato';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (Array.isArray(value)) {
    return value.map(formatProfileValue).filter((item) => item !== 'Sin dato').join(', ');
  }
  if (isRecord(value)) return JSON.stringify(value);
  return String(value);
};

const coerceConfidence = (value: unknown): number | undefined => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return Math.max(0, Math.min(numeric, 1));
};

const formatProfileDate = (value: unknown): string | undefined => {
  if (!hasMeaningfulValue(value)) return undefined;
  try {
    return format(parseISO(String(value)), 'dd/MM/yyyy HH:mm', { locale: es });
  } catch {
    return String(value);
  }
};

const getFollowUpKindLabel = (kind?: string): string => {
  switch (kind) {
    case 'stale_mid_followup':
      return 'Seguimiento IA';
    case 'stale_mid_followup_deferred':
      return 'Seguimiento diferido';
    case 'csat_followup':
      return 'Encuesta CSAT';
    case 'close_after_csat':
      return 'Cierre automático';
    case 'initial_reengagement':
      return 'Reenganche inicial sin respuesta';
    case 'engaged_reengagement':
      return 'Reenganche de conversación activa';
    case 'close_after_stale_reengagement':
      return 'Cierre tras reenganche';
    case 'close_after_decline':
      return 'Cierre de pérdida pendiente';
    case 'close_after_followup_skip':
      return 'Cierre tras omitir seguimiento';
    default:
      return 'Acción automática';
  }
};

const getFollowUpStatusLabel = (status?: string): string => {
  switch (status) {
    case 'pending':
      return 'Programado';
    case 'running':
      return 'En proceso';
    case 'completed':
      return 'Completado';
    case 'cancelled':
      return 'Cancelado';
    case 'failed':
      return 'Fallido';
    default:
      return 'Sin acción';
  }
};

const getFollowUpStatusTone = (status?: string): { bg: string; color: string } => {
  switch (status) {
    case 'pending':
      return { bg: 'rgba(25, 118, 210, 0.12)', color: '#0b57d0' };
    case 'running':
      return { bg: 'rgba(0, 168, 132, 0.12)', color: chatColors.accentDark };
    case 'completed':
      return { bg: 'rgba(46, 125, 50, 0.12)', color: '#2e7d32' };
    case 'failed':
      return { bg: 'rgba(179, 38, 30, 0.12)', color: '#b3261e' };
    case 'cancelled':
      return { bg: 'rgba(102, 119, 129, 0.14)', color: chatColors.meta };
    default:
      return { bg: 'rgba(102, 119, 129, 0.12)', color: chatColors.meta };
  }
};

const formatFollowUpDate = (value?: string): string => {
  if (!value) return 'Sin fecha';
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm', { locale: es });
  } catch {
    return value;
  }
};

const getProfileSnapshotEntries = (
  snapshot?: Record<string, unknown>
): ProfileSnapshotEntry[] => {
  if (!snapshot) return [];

  return Object.entries(snapshot)
    .flatMap(([key, rawField]) => {
      if (!isRecord(rawField)) {
        if (!hasMeaningfulValue(rawField)) return [];
        return [
          {
            key,
            label: humanizeProfileKey(key),
            value: formatProfileValue(rawField),
          },
        ];
      }

      const confidence = coerceConfidence(rawField.confidence);
      const hasValue = hasMeaningfulValue(rawField.value);
      if (!hasValue && !confidence) return [];

      return [
        {
          key,
          label: humanizeProfileKey(key),
          value: formatProfileValue(rawField.value),
          confidence,
          source: hasMeaningfulValue(rawField.source) ? String(rawField.source) : undefined,
          updatedAt: formatProfileDate(rawField.updated_at),
        },
      ];
    });
};

const getProfileSnapshotGroups = (
  entries: ProfileSnapshotEntry[]
): ProfileSnapshotGroup[] => {
  const remaining = new Map(entries.map((entry) => [entry.key, entry]));
  const grouped = profileGroups
    .map((group) => {
      const groupEntries = group.fields
        .map((field) => remaining.get(field))
        .filter((entry): entry is ProfileSnapshotEntry => Boolean(entry));
      group.fields.forEach((field) => remaining.delete(field));
      return {
        title: group.title,
        entries: groupEntries,
      };
    })
    .filter((group) => group.entries.length > 0);

  if (remaining.size > 0) {
    grouped.push({
      title: 'Otros atributos',
      entries: Array.from(remaining.values()),
    });
  }

  return grouped;
};

const hasExportableConversationSummary = (
  conversation: ConversationDetailType
): boolean => {
  const hasAdvisorSummary = [
    conversation.perfil_del_cliente,
    conversation.resumen_de_conversacion,
    conversation.observaciones,
  ].some((value) => Boolean(value?.trim()));
  const hasProfileValue = Object.entries(conversation.profile_snapshot_json || {}).some(
    ([key, rawField]) => {
      if (['source', 'updated_at'].includes(key)) return false;
      return isRecord(rawField)
        ? hasMeaningfulValue(rawField.value)
        : hasMeaningfulValue(rawField);
    }
  );
  return hasAdvisorSummary || hasProfileValue;
};

const ConversationDetail: React.FC<ConversationDetailProps> = ({
  conversationId,
  archiveStatus,
  onBack,
  onArchiveStateChange,
}) => {
  const { hasPermission } = useAuth();
  const { chatbotName } = useAppMeta();
  const canManageConversations = hasPermission('manage_conversations');
  const canExportConversations = hasPermission('export_conversations');
  const [conversation, setConversation] = useState<ConversationDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [manualMessage, setManualMessage] = useState('');
  const [sendingManual, setSendingManual] = useState(false);
  const [updatingAiControl, setUpdatingAiControl] = useState(false);
  const [updatingArchiveState, setUpdatingArchiveState] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [includeSummaryInPdf, setIncludeSummaryInPdf] = useState(false);
  const [includeTranscriptInPdf, setIncludeTranscriptInPdf] = useState(true);
  const [downloadingConversationPdf, setDownloadingConversationPdf] = useState(false);
  const [conversationPdfError, setConversationPdfError] = useState<string | null>(null);
  const [takeoverNotice, setTakeoverNotice] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadConversationDetail = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
        setError(null);
      }
      const data = await conversationService.getConversationDetail(conversationId);
      setConversation(data);
    } catch (err) {
      console.error('Error loading conversation detail:', err);
      if (showLoading) {
        setError('Error al cargar los detalles de la conversación.');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [conversationId]);

  useEffect(() => {
    loadConversationDetail();
  }, [loadConversationDetail]);

  useEffect(() => {
    // Scroll to bottom when messages load
    if (conversation?.messages && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation?.messages]);

  // Subscribe to WebSocket updates for real-time messages
  useEffect(() => {
    const handleNewMessage = (event: any) => {
      if (String(event.conversation_id) !== conversationId) {
        return;
      }

      if (event.type === 'message_updated') {
        setConversation((prevConversation) => {
          if (!prevConversation) return prevConversation;

          return {
            ...prevConversation,
            delivery_status: event.conversation?.delivery_status ?? prevConversation.delivery_status,
            messages: prevConversation.messages.map((message) =>
              message.id === event.message_id
                ? {
                    ...message,
                    content: event.content ?? message.content,
                    message_type: event.message_type ?? message.message_type,
                    audio_duration: event.audio_duration ?? message.audio_duration,
                    file_path: event.file_path ?? message.file_path,
                    whatsapp_message_id: event.whatsapp_message_id ?? message.whatsapp_message_id,
                    campaign_id: event.campaign_id ?? message.campaign_id,
                    wa_status: event.wa_status ?? message.wa_status,
                    wa_status_accepted_at:
                      event.wa_status_accepted_at ?? message.wa_status_accepted_at,
                    wa_status_sent_at: event.wa_status_sent_at ?? message.wa_status_sent_at,
                    wa_status_delivered_at:
                      event.wa_status_delivered_at ?? message.wa_status_delivered_at,
                    wa_status_read_at: event.wa_status_read_at ?? message.wa_status_read_at,
                    wa_status_failed_at:
                      event.wa_status_failed_at ?? message.wa_status_failed_at,
                    wa_error_code: event.wa_error_code ?? message.wa_error_code,
                    wa_error_description:
                      event.wa_error_description ?? message.wa_error_description,
                    retry_count: event.retry_count ?? message.retry_count,
                    max_retries: event.max_retries ?? message.max_retries,
                    next_retry_at: event.next_retry_at ?? message.next_retry_at,
                    retry_exhausted: event.retry_exhausted ?? message.retry_exhausted,
                    timestamp: event.timestamp ?? message.timestamp,
                  }
                : message
            ),
          };
        });
        void loadConversationDetail(false);
        return;
      }

      if (event.type === 'new_message') {
        // Add new message to the conversation
        setConversation((prevConversation) => {
          if (!prevConversation) return prevConversation;
          if (prevConversation.messages.some((msg) => msg.id === event.message_id)) {
            return prevConversation;
          }

          const newMessage: Message = {
            id: event.message_id,
            conversation_id: prevConversation.id,
            content: event.content,
            sender: event.sender,
            receiver: event.sender === 'user' ? 'bot' : 'user',
            message_type: event.message_type || 'text',
            message_category: event.message_category || undefined,
            audio_duration: event.audio_duration ?? undefined,
            file_path: event.file_path ?? undefined,
            is_human_takeover: Boolean(event.is_human_takeover),
            human_sender_name: event.human_sender_name || undefined,
            whatsapp_message_id: event.whatsapp_message_id || undefined,
            campaign_id: event.campaign_id || undefined,
            wa_status: event.wa_status || undefined,
            wa_status_accepted_at: event.wa_status_accepted_at || undefined,
            wa_status_sent_at: event.wa_status_sent_at || undefined,
            wa_status_delivered_at: event.wa_status_delivered_at || undefined,
            wa_status_read_at: event.wa_status_read_at || undefined,
            wa_status_failed_at: event.wa_status_failed_at || undefined,
            wa_error_code: event.wa_error_code ?? undefined,
            wa_error_description: event.wa_error_description || undefined,
            retry_count: event.retry_count ?? undefined,
            max_retries: event.max_retries ?? undefined,
            next_retry_at: event.next_retry_at || undefined,
            retry_exhausted: event.retry_exhausted ?? undefined,
            timestamp: event.timestamp,
            created_at: new Date().toISOString(),
            is_flagged: false,
          };

          return {
            ...prevConversation,
            messages: [...prevConversation.messages, newMessage],
            message_count: event.conversation?.message_count || (prevConversation.message_count + 1),
            ai_enabled: event.conversation?.ai_enabled ?? prevConversation.ai_enabled,
            delivery_status: event.conversation?.delivery_status ?? prevConversation.delivery_status,
          };
        });

        // Auto-scroll to bottom when new message arrives
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
        void loadConversationDetail(false);
      }
    };

    // Subscribe to conversations topic
    const unsubscribe = websocketService.subscribe('conversations', handleNewMessage);

    return () => {
      // Unsubscribe when component unmounts
      unsubscribe();
    };
  }, [conversationId, loadConversationDetail]);

  const getSenderLabel = (message: Message): string => {
    if (message.sender === 'user') return 'Cliente';
    if (message.is_human_takeover) {
      return message.human_sender_name
        ? `Asesor (${message.human_sender_name})`
        : 'Asesor';
    }
    return chatbotName;
  };

  const handleAiControlToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!conversation) return;

    const nextAiEnabled = event.target.checked;
    try {
      setUpdatingAiControl(true);
      setActionError(null);
      await conversationService.setConversationAiControl(conversation.id, nextAiEnabled);
      setConversation((prevConversation) => {
        if (!prevConversation) return prevConversation;
        return {
          ...prevConversation,
          ai_enabled: nextAiEnabled,
        };
      });
    } catch (err) {
      console.error('Error updating conversation AI control:', err);
      setActionError('No se pudo actualizar el estado del agente IA.');
    } finally {
      setUpdatingAiControl(false);
    }
  };

  const handleArchiveToggle = async () => {
    if (!conversation || updatingArchiveState) return;

    const nextArchivedState = !(conversation.is_archived ?? false);
    try {
      setUpdatingArchiveState(true);
      setActionError(null);
      const result = await conversationService.setConversationArchived(
        conversation.id,
        nextArchivedState
      );

      setConversation((prevConversation) => {
        if (!prevConversation) {
          return prevConversation;
        }

        return {
          ...prevConversation,
          is_archived: result.is_archived,
          archived_at: result.archived_at,
          archived_by: result.archived_by,
          is_active: result.is_archived ? false : prevConversation.is_active,
        };
      });

      onArchiveStateChange(result.is_archived);
    } catch (err) {
      console.error('Error updating archive state:', err);
      setActionError('No se pudo actualizar el estado de archivado de la conversación.');
    } finally {
      setUpdatingArchiveState(false);
    }
  };

  const handleSendManualMessage = async () => {
    const messageToSend = manualMessage.trim();
    if (!conversation || !messageToSend || sendingManual) return;

    try {
      setSendingManual(true);
      setActionError(null);

      const result = await conversationService.sendManualMessage(conversation.id, {
        message: messageToSend,
      });

      setConversation((prevConversation) => {
        if (!prevConversation) return prevConversation;
        if (prevConversation.messages.some((msg) => msg.id === result.stored_message.id)) {
          return {
            ...prevConversation,
            ai_enabled: result.ai_enabled,
          };
        }

        return {
          ...prevConversation,
          ai_enabled: result.ai_enabled,
          message_count: prevConversation.message_count + 1,
          messages: [...prevConversation.messages, result.stored_message],
        };
      });
      setManualMessage('');

      if (result.ai_auto_disabled) {
        setTakeoverNotice('La IA fue desactivada automáticamente para permitir la toma de control del asesor.');
      }

      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error sending manual message:', err);
      setActionError('No se pudo enviar el mensaje manual al cliente.');
    } finally {
      setSendingManual(false);
    }
  };

  const handleManualMessageKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSendManualMessage();
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const buildFallbackConversationPdfFilename = (): string => {
    const safeCustomerName = (conversation?.customer_name || 'Cliente')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9 _-]/gi, ' ')
      .trim()
      .replace(/\s+/g, ' ') || 'Cliente';
    return `Conversacion - ${safeCustomerName} - ${format(new Date(), 'dd-MM-yyyy')}.pdf`;
  };

  const handleOpenExportDialog = () => {
    setIncludeSummaryInPdf(
      conversation ? hasExportableConversationSummary(conversation) : false
    );
    setIncludeTranscriptInPdf(true);
    setConversationPdfError(null);
    setExportDialogOpen(true);
  };

  const handleExportConversationPdf = async () => {
    if (
      !conversation ||
      downloadingConversationPdf ||
      (!includeSummaryInPdf && !includeTranscriptInPdf)
    ) return;

    try {
      setDownloadingConversationPdf(true);
      setConversationPdfError(null);
      const { blob, filename } = await conversationService.exportConversationPdf(conversation.id, {
        include_summary: includeSummaryInPdf,
        include_transcript: includeTranscriptInPdf,
      });
      downloadBlob(blob, filename || buildFallbackConversationPdfFilename());
      setExportDialogOpen(false);
    } catch (err) {
      console.error('Error exporting conversation report:', err);
      setConversationPdfError('No se pudo descargar el reporte de la conversación.');
    } finally {
      setDownloadingConversationPdf(false);
    }
  };

  const handleRetryManualMessage = async (message: Message) => {
    if (!conversation) {
      return;
    }

    try {
      setActionError(null);
      const result = await conversationService.retryManualMessage(conversation.id, message.id);
      setConversation((prevConversation) => {
        if (!prevConversation) return prevConversation;
        return {
          ...prevConversation,
          messages: prevConversation.messages.map((currentMessage) =>
            currentMessage.id === message.id ? result.stored_message : currentMessage
          ),
        };
      });
    } catch (err) {
      console.error('Error retrying manual message:', err);
      setActionError('No se pudo reintentar el mensaje manual.');
    }
  };

  const getMessageIcon = (message: Message) => {
    switch (message.message_type) {
      case 'audio':
        return <VolumeUpIcon fontSize="small" />;
      case 'image':
        return <ImageIcon fontSize="small" />;
      case 'document':
        return <DescriptionIcon fontSize="small" />;
      default:
        return <MessageIcon fontSize="small" />;
    }
  };

  const formatPhoneNumber = (phone?: string): string => {
    if (!phone) return 'N/A';
    if (phone.startsWith('57') && phone.length === 12) {
      return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`;
    }
    return phone;
  };

  const formatLossReason = (reason?: string | null): string => {
    const reasonMap: Record<string, string> = {
      delivery_failed: 'No fue posible contactarlo',
      no_engagement: 'Sin contacto efectivo',
      abandoned: 'Abandonó',
      declined: 'Declinó',
    };
    return reason ? reasonMap[reason] || reason : 'Sin clasificar';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!conversation) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Conversación no encontrada.
      </Alert>
    );
  }

  const advisorSummaryItems = [
    { label: 'Perfil del Cliente', value: conversation.perfil_del_cliente },
    { label: 'Resumen de Conversación', value: conversation.resumen_de_conversacion },
    { label: 'Observaciones', value: conversation.observaciones },
  ].filter((item) => Boolean(item.value?.trim()));
  const profileSnapshotEntries = getProfileSnapshotEntries(conversation.profile_snapshot_json);
  const profileSnapshotGroups = getProfileSnapshotGroups(profileSnapshotEntries);
  const hasExportableSummary = hasExportableConversationSummary(conversation);
  const nextProactiveFollowUp = conversation.proactive_followup?.next_action ?? null;
  const lastProactiveFollowUp = conversation.proactive_followup?.last_outcome ?? null;
  const followUpTone = getFollowUpStatusTone(nextProactiveFollowUp?.status);
  const pendingLostClassification =
    conversation.lead_status === 'lost' &&
    !conversation.loss_reason &&
    nextProactiveFollowUp?.kind === 'close_after_decline';

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          Detalles de Conversación
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Conversation Info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                {/* Customer Info */}
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {conversation.customer_name || 'Cliente Anónimo'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {conversation.user_id}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {/* Contact Info */}
                <Box display="flex" alignItems="center" gap={2}>
                  <PhoneIcon color="action" />
                  <Typography variant="body2">
                    {formatPhoneNumber(conversation.customer_phone)}
                  </Typography>
                </Box>

                {/* Lead Status */}
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    Estado:
                  </Typography>
                  <Chip
                    label={conversationService.formatLeadStatus(conversation.lead_status)}
                    size="small"
                    sx={{
                      backgroundColor: conversationService.getLeadStatusColor(conversation.lead_status),
                      color: 'white',
                      fontWeight: 'medium',
                    }}
                  />
                </Box>

                {conversation.lead_status === 'lost' && (
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        Motivo:
                      </Typography>
                      <Chip
                        label={
                          pendingLostClassification
                            ? 'Pendiente de cierre'
                            : formatLossReason(conversation.loss_reason)
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {conversation.decline_reason && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Causa de no venta
                        </Typography>
                        <Typography variant="body2">
                          {conversation.decline_reason}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}

                {(conversation.is_archived ?? false) && (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                      Archivo:
                    </Typography>
                    <Chip
                      label="Archivada"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  </Box>
                )}

                {/* Project Interest */}
                {conversation.project_interest && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Proyecto de Interés:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {conversation.project_interest}
                    </Typography>
                  </Box>
                )}

                <Divider />

                {/* Timing Info */}
                <Box display="flex" alignItems="center" gap={2}>
                  <ScheduleIcon color="action" />
                  <Box>
                    <Typography variant="body2">
                      Inicio: {format(parseISO(conversation.start_time), 'dd/MM/yyyy HH:mm', { locale: es })}
                    </Typography>
                    {conversation.end_time && (
                      <Typography variant="body2">
                        Fin: {format(parseISO(conversation.end_time), 'dd/MM/yyyy HH:mm', { locale: es })}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: nextProactiveFollowUp ? 'primary.light' : 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    bgcolor: nextProactiveFollowUp
                      ? 'rgba(25, 118, 210, 0.04)'
                      : 'background.default',
                  }}
                >
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Typography variant="body2" fontWeight={700}>
                        Próxima acción automática
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          nextProactiveFollowUp
                            ? getFollowUpStatusLabel(nextProactiveFollowUp.status)
                            : 'Sin acción'
                        }
                        sx={{
                          bgcolor: followUpTone.bg,
                          color: followUpTone.color,
                          fontWeight: 700,
                        }}
                      />
                    </Box>

                    {nextProactiveFollowUp ? (
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {getFollowUpKindLabel(nextProactiveFollowUp.kind)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Programado para {formatFollowUpDate(nextProactiveFollowUp.due_at)}
                        </Typography>
                        {nextProactiveFollowUp.attempt_count > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Intentos: {nextProactiveFollowUp.attempt_count}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Sin acción programada
                        </Typography>
                        {lastProactiveFollowUp?.reason && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Último estado: {lastProactiveFollowUp.reason}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Message Count */}
                <Box display="flex" alignItems="center" gap={2}>
                  <MessageIcon color="action" />
                  <Typography variant="body2">
                    {conversation.message_count} mensajes
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={2}>
                  <PersonIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Asesor receptor
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                      {conversation.hubspot_contact_owner_email_at_start || 'Sin dato'}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Agente IA:
                  </Typography>
                  {canManageConversations ? (
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={conversation.ai_enabled ?? true}
                          onChange={handleAiControlToggle}
                          disabled={updatingAiControl}
                        />
                      }
                      label={(conversation.ai_enabled ?? true) ? 'Activo' : 'Pausado'}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label={(conversation.ai_enabled ?? true) ? 'Activo' : 'Pausado'}
                      variant="outlined"
                    />
                  )}
                </Box>

                {canManageConversations && (
                  <Box>
                    <Button
                      fullWidth
                      variant={conversation.is_archived ? 'outlined' : 'contained'}
                      color={conversation.is_archived ? 'inherit' : 'inherit'}
                      startIcon={conversation.is_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                      onClick={handleArchiveToggle}
                      disabled={updatingArchiveState}
                    >
                      {conversation.is_archived ? 'Restaurar conversación' : 'Archivar conversación'}
                    </Button>
                    {archiveStatus !== 'all' && (
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        Vista actual: conversaciones {archiveStatus === 'active' ? 'activas' : 'archivadas'}.
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Analytics */}
                {conversation.analytics && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight="bold">
                      Análisis
                    </Typography>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Mensajes del usuario: {conversation.analytics.user_messages}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Mensajes del bot: {conversation.analytics.bot_messages}
                      </Typography>
                    </Box>

                    {conversation.analytics.avg_response_time && (
                      <Typography variant="body2" color="text.secondary">
                        Tiempo promedio de respuesta: {Math.round(conversation.analytics.avg_response_time)}s
                      </Typography>
                    )}

                    {conversation.analytics.sentiment_score && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          Sentimiento:
                        </Typography>
                        <Chip
                          label={
                            conversation.analytics.sentiment_score > 0.1
                              ? 'Positivo'
                              : conversation.analytics.sentiment_score < -0.1
                              ? 'Negativo'
                              : 'Neutral'
                          }
                          size="small"
                          color={
                            conversation.analytics.sentiment_score > 0.1
                              ? 'success'
                              : conversation.analytics.sentiment_score < -0.1
                              ? 'error'
                              : 'default'
                          }
                        />
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Messages and Analytics */}
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              height: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: chatColors.frame,
              border: `1px solid ${chatColors.border}`,
            }}
          >
            <Box borderBottom={1} borderColor={chatColors.border} bgcolor={chatColors.frame}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  px: 1,
                  '& .MuiTab-root': {
                    minHeight: 56,
                    color: chatColors.meta,
                    fontWeight: 600,
                  },
                  '& .Mui-selected': {
                    color: chatColors.accent,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: chatColors.accent,
                  },
                }}
              >
                <Tab
                  icon={<MessageIcon />}
                  label="Mensajes"
                  iconPosition="start"
                />
                <Tab
                  icon={<AnalyticsIcon />}
                  label="Analíticas"
                  iconPosition="start"
                />
                <Tab
                  icon={<SmartToyIcon />}
                  label="HubSpot / Perfil"
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  bgcolor: chatColors.frame,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    borderBottom: `1px solid ${chatColors.border}`,
                    bgcolor: chatColors.frame,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ bgcolor: chatColors.accent, color: 'white' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: chatColors.text }}>
                        {conversation.customer_name || 'Cliente Anónimo'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: chatColors.meta }}>
                        {formatPhoneNumber(conversation.customer_phone)}
                        {conversation.project_interest ? ` • ${conversation.project_interest}` : ''}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {conversation.delivery_status && (
                      <Chip
                        size="small"
                        label={
                          conversation.delivery_status === 'pending_delivery'
                            ? 'Pendiente de entrega'
                            : conversation.delivery_status === 'unreachable'
                            ? 'No entregada'
                            : conversation.delivery_status === 'opted_out'
                            ? 'Opt-out'
                            : 'Activa'
                        }
                        sx={{
                          bgcolor:
                            conversation.delivery_status === 'active'
                              ? 'rgba(0, 168, 132, 0.12)'
                              : conversation.delivery_status === 'pending_delivery'
                              ? 'rgba(180, 120, 0, 0.12)'
                              : 'rgba(179, 38, 30, 0.12)',
                          color:
                            conversation.delivery_status === 'active'
                              ? chatColors.accentDark
                              : conversation.delivery_status === 'pending_delivery'
                              ? '#8a5200'
                              : '#b3261e',
                          fontWeight: 700,
                          borderRadius: 3,
                        }}
                      />
                    )}
                    <Chip
                      icon={<SmartToyIcon sx={{ color: 'inherit !important' }} />}
                      label={(conversation.ai_enabled ?? true) ? 'IA activa' : 'IA pausada'}
                      size="small"
                      sx={{
                        bgcolor: (conversation.ai_enabled ?? true)
                          ? 'rgba(0, 168, 132, 0.12)'
                          : 'rgba(102, 119, 129, 0.14)',
                        color: (conversation.ai_enabled ?? true) ? chatColors.accentDark : chatColors.meta,
                        fontWeight: 700,
                        borderRadius: 3,
                      }}
                    />
                  </Stack>
                </Box>

                <Box
                  sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    px: { xs: 1.5, sm: 2.5 },
                    py: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    bgcolor: chatColors.canvas,
                    backgroundImage: `
                      radial-gradient(circle at 24px 24px, ${chatColors.canvasTint} 2px, transparent 0),
                      radial-gradient(circle at 0 0, ${chatColors.canvasShadow} 1px, transparent 0),
                      linear-gradient(180deg, rgba(255,255,255,0.24), rgba(239,234,226,0.24))
                    `,
                    backgroundSize: '48px 48px, 32px 32px, 100% 100%',
                  }}
                >
                  {takeoverNotice && (
                    <Box display="flex" justifyContent="center" mb={0.5}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5,
                          py: 0.75,
                          maxWidth: 520,
                          bgcolor: chatColors.systemNotice,
                          color: chatColors.meta,
                          borderRadius: 3,
                          boxShadow: '0 1px 0 rgba(17, 27, 33, 0.08)',
                        }}
                      >
                        <Typography variant="caption" sx={{ flexGrow: 1, lineHeight: 1.5 }}>
                          {takeoverNotice}
                        </Typography>
                        <IconButton size="small" onClick={() => setTakeoverNotice(null)}>
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {actionError && (
                    <Box display="flex" justifyContent="center" mb={0.5}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5,
                          py: 0.75,
                          maxWidth: 520,
                          bgcolor: 'rgba(255, 233, 231, 0.92)',
                          color: '#8a2f2b',
                          borderRadius: 3,
                          boxShadow: '0 1px 0 rgba(17, 27, 33, 0.08)',
                        }}
                      >
                        <Typography variant="caption" sx={{ flexGrow: 1, lineHeight: 1.5 }}>
                          {actionError}
                        </Typography>
                        <IconButton size="small" onClick={() => setActionError(null)}>
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {conversation.messages.length === 0 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          bgcolor: chatColors.systemNotice,
                          color: chatColors.meta,
                          borderRadius: 3,
                          boxShadow: '0 1px 0 rgba(17, 27, 33, 0.08)',
                        }}
                      >
                        <Typography variant="caption">
                          Esta conversación todavía no tiene mensajes visibles.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {conversation.messages.map((message, index) => {
                    const incoming = isIncomingMessage(message);
                    const proactive =
                      !incoming && message.message_category === 'proactive_followup';
                    const deliveryIndicator = getDeliveryIndicator(message);
                    const previousMessage = conversation.messages[index - 1];
                    const previousDayKey = previousMessage
                      ? getDayKey(previousMessage.timestamp)
                      : null;
                    const showDayDivider =
                      index === 0 || previousDayKey !== getDayKey(message.timestamp);
                    const footerMetadata: string[] = [];
                    const hasAttachedAudio =
                      typeof message.file_path === 'string' && message.file_path.includes('audio/');

                    if (message.confidence_score) {
                      footerMetadata.push(`Confianza ${Math.round(message.confidence_score * 100)}%`);
                    }
                    if (message.audio_duration) {
                      footerMetadata.push(`Duración ${Math.round(message.audio_duration)}s`);
                    }

                    return (
                      <React.Fragment key={message.id}>
                        {showDayDivider && (
                          <Box display="flex" justifyContent="center" py={0.75}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.6,
                                bgcolor: 'rgba(255, 255, 255, 0.72)',
                                color: chatColors.meta,
                                borderRadius: 3,
                                boxShadow: '0 1px 0 rgba(17, 27, 33, 0.08)',
                                textTransform: 'capitalize',
                              }}
                            >
                              <Typography variant="caption">
                                {getDayLabel(message.timestamp)}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        <Box display="flex" justifyContent={incoming ? 'flex-start' : 'flex-end'}>
                          <Paper
                            elevation={0}
                            sx={{
                              maxWidth: { xs: '88%', sm: '76%' },
                              px: 1.5,
                              py: 1.25,
                              bgcolor: incoming
                                ? chatColors.incoming
                                : proactive
                                  ? chatColors.proactive
                                  : chatColors.outgoing,
                              color: chatColors.text,
                              borderRadius: incoming ? '18px 18px 18px 6px' : '18px 18px 6px 18px',
                              boxShadow: '0 1px 0 rgba(17, 27, 33, 0.08)',
                            }}
                          >
                            {!incoming && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mb: 0.75,
                                  color: proactive
                                    ? chatColors.proactiveAccent
                                    : chatColors.accentDark,
                                  fontWeight: 700,
                                }}
                              >
                                {getSenderLabel(message)}
                                {proactive && ' · Seguimiento proactivo de la IA'}
                              </Typography>
                            )}

                            {message.message_type !== 'text' && (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  px: 1,
                                  py: 0.4,
                                  mb: 1,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(17, 27, 33, 0.06)',
                                  color: chatColors.meta,
                                }}
                              >
                                {getMessageIcon(message)}
                                <Typography variant="caption" fontWeight={600}>
                                  {conversationService.formatMessageType(message.message_type)}
                                </Typography>
                              </Box>
                            )}

                            {message.message_type === 'audio' ? (
                              <Box>
                                <AudioPlayer
                                  conversationId={conversationId}
                                  messageId={message.id}
                                  duration={message.audio_duration}
                                  variant="chat"
                                  tone={incoming ? 'inbound' : 'outbound'}
                                  onError={(audioError) => console.error('Audio player error:', audioError)}
                                />
                                {message.content && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: 'pre-wrap',
                                      mt: 1,
                                      color: chatColors.meta,
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    Transcripcion: {message.content}
                                  </Typography>
                                )}
                              </Box>
                            ) : hasAttachedAudio ? (
                              <Box>
                                <AudioPlayer
                                  conversationId={conversationId}
                                  messageId={message.id}
                                  duration={message.audio_duration}
                                  variant="chat"
                                  tone={incoming ? 'inbound' : 'outbound'}
                                  onError={(audioError) => console.error('Audio player error:', audioError)}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.5,
                                    wordBreak: 'break-word',
                                    mt: 1,
                                  }}
                                >
                                  {message.content || 'Sin contenido disponible.'}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  lineHeight: 1.5,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {message.content || 'Sin contenido disponible.'}
                              </Typography>
                            )}

                            {message.campaign_id && (
                              <Box mt={1}>
                                <Chip
                                  size="small"
                                  icon={<CampaignIcon sx={{ fontSize: 14 }} />}
                                  label={`Campaña: ${message.campaign_id}`}
                                  component="a"
                                  clickable
                                  href={`/campaigns/history?campaignId=${encodeURIComponent(message.campaign_id)}`}
                                  sx={{
                                    bgcolor: 'rgba(11, 87, 208, 0.08)',
                                    color: '#0b57d0',
                                    fontWeight: 600,
                                  }}
                                />
                              </Box>
                            )}

                            {(message.is_flagged || footerMetadata.length > 0) && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75,
                                  mt: 1,
                                }}
                              >
                                {message.is_flagged && (
                                  <Tooltip title="Mensaje marcado">
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 0.9,
                                        py: 0.35,
                                        borderRadius: 999,
                                        bgcolor: 'rgba(211, 47, 47, 0.1)',
                                        color: '#b3261e',
                                      }}
                                    >
                                      <FlagIcon sx={{ fontSize: 14 }} />
                                      <Typography variant="caption" fontWeight={600}>
                                        Marcado
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                )}
                                {footerMetadata.map((item) => (
                                  <Box
                                    key={item}
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      px: 0.9,
                                      py: 0.35,
                                      borderRadius: 999,
                                      bgcolor: 'rgba(17, 27, 33, 0.06)',
                                      color: chatColors.meta,
                                    }}
                                  >
                                    <Typography variant="caption" fontWeight={600}>
                                      {item}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.75} gap={1}>
                              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                {!incoming && deliveryIndicator && (
                                  <Tooltip
                                    title={
                                      message.wa_status === 'failed'
                                        ? humanizeErrorReason(message)
                                        : deliveryIndicator.label
                                    }
                                  >
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.35,
                                        color: deliveryIndicator.color,
                                      }}
                                    >
                                      {deliveryIndicator.icon}
                                    </Box>
                                  </Tooltip>
                                )}
                                {!incoming && isRetryableManualFailure(message) && (
                                  <Tooltip title="Reintentar envío">
                                    <IconButton
                                      size="small"
                                      aria-label="Reintentar mensaje manual"
                                      onClick={() => void handleRetryManualMessage(message)}
                                      sx={{ color: '#8a5200', p: 0.25 }}
                                    >
                                      <RetryIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: chatColors.meta,
                                  fontSize: '0.72rem',
                                }}
                              >
                                {format(parseISO(message.timestamp), 'HH:mm', { locale: es })}
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>

                {canManageConversations && (
                  <Box
                    sx={{
                      p: 2,
                      borderTop: `1px solid ${chatColors.border}`,
                      bgcolor: chatColors.frame,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-end">
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        value={manualMessage}
                        onChange={(event) => setManualMessage(event.target.value)}
                        onKeyDown={handleManualMessageKeyDown}
                        placeholder="Escribe una respuesta manual para el cliente..."
                        disabled={sendingManual}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 6,
                            bgcolor: '#ffffff',
                            alignItems: 'flex-end',
                            pr: 0.5,
                            boxShadow: '0 1px 0 rgba(17, 27, 33, 0.06)',
                            '& fieldset': {
                              borderColor: 'transparent',
                            },
                            '&:hover fieldset': {
                              borderColor: 'transparent',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: chatColors.accent,
                            },
                          },
                        }}
                      />
                      <Tooltip title="Enviar mensaje manual">
                        <span>
                          <IconButton
                            onClick={handleSendManualMessage}
                            disabled={sendingManual || !manualMessage.trim()}
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: chatColors.accent,
                              color: 'white',
                              boxShadow: '0 1px 2px rgba(17, 27, 33, 0.18)',
                              '&:hover': {
                                bgcolor: chatColors.accentDark,
                              },
                              '&.Mui-disabled': {
                                bgcolor: 'rgba(0, 168, 132, 0.3)',
                                color: 'rgba(255, 255, 255, 0.9)',
                              },
                            }}
                          >
                            <SendIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: 'auto',
                  p: 2,
                }}
              >
                <ConversationAnalytics conversationId={conversationId} />
              </Box>
            )}

            {activeTab === 2 && (
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: 'auto',
                  p: 2,
                  bgcolor: '#f7f8fa',
                }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        HubSpot / Perfil
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Datos perfilados para seguimiento comercial.
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        canExportConversations
                          ? 'Descargar reporte PDF'
                          : 'No tienes permisos para exportar conversaciones'
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          disabled={!canExportConversations}
                          onClick={handleOpenExportDialog}
                          sx={{ borderRadius: '8px', whiteSpace: 'nowrap' }}
                        >
                          Descargar PDF
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>

                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      border: '1px solid #e0e4e7',
                      borderRadius: '8px',
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Resumen para asesor
                    </Typography>

                    {advisorSummaryItems.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Sin resumen para asesor todavía.
                      </Alert>
                    ) : (
                      <Stack spacing={1.5}>
                        {advisorSummaryItems.map((item) => (
                          <Box key={item.label}>
                            <Typography variant="body2" color="text.secondary">
                              {item.label}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                              {item.value}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      border: '1px solid #e0e4e7',
                      borderRadius: '8px',
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      HubSpot
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          HubSpot Contact ID
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                          {conversation.hubspot_contact_id || 'Sin dato'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          HubSpot Deal ID
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                          {conversation.hubspot_deal_id || 'Sin dato'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Nombre del proyecto
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                          {conversation.hubspot_deal_project_name || 'Sin dato'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      border: '1px solid #e0e4e7',
                      borderRadius: '8px',
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Perfil detectado
                    </Typography>

                    {profileSnapshotGroups.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Sin atributos perfilados todavía.
                      </Alert>
                    ) : (
                      <Stack spacing={2}>
                        {profileSnapshotGroups.map((group) => (
                          <Box key={group.title}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                              {group.title}
                            </Typography>
                            <Grid container spacing={1.5}>
                              {group.entries.map((entry) => (
                                <Grid item xs={12} sm={6} key={entry.key}>
                                  <Box
                                    sx={{
                                      border: '1px solid #e0e4e7',
                                      borderRadius: '8px',
                                      p: 1.5,
                                      minHeight: 118,
                                      bgcolor: '#fbfcfd',
                                    }}
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      {entry.label}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight={700}
                                      sx={{ mt: 0.5, wordBreak: 'break-word' }}
                                    >
                                      {entry.value}
                                    </Typography>
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                      {entry.confidence !== undefined && (
                                        <Chip
                                          size="small"
                                          label={`${Math.round(entry.confidence * 100)}% confianza`}
                                          variant="outlined"
                                          sx={{ borderRadius: '8px' }}
                                        />
                                      )}
                                      {entry.source && (
                                        <Chip
                                          size="small"
                                          label={entry.source}
                                          variant="outlined"
                                          sx={{ borderRadius: '8px' }}
                                        />
                                      )}
                                    </Stack>
                                    {entry.updatedAt && (
                                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                        Actualizado: {entry.updatedAt}
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={exportDialogOpen}
        onClose={downloadingConversationPdf ? undefined : () => setExportDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Exportar conversación</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecciona las secciones que quieres incluir en un único PDF.
          </Typography>

          <Stack spacing={1.5}>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeSummaryInPdf}
                    onChange={(event) => setIncludeSummaryInPdf(event.target.checked)}
                    disabled={!hasExportableSummary || downloadingConversationPdf}
                  />
                }
                label="Resumen HubSpot / Perfil"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                {hasExportableSummary
                  ? 'Incluye el resumen para asesor y el perfil detectado.'
                  : 'No hay información de resumen o perfil disponible.'}
              </Typography>
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeTranscriptInPdf}
                    onChange={(event) => setIncludeTranscriptInPdf(event.target.checked)}
                    disabled={downloadingConversationPdf}
                  />
                }
                label="Conversación completa"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Incluye todos los mensajes visibles en orden cronológico.
              </Typography>
            </Box>
          </Stack>

          {conversationPdfError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {conversationPdfError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setExportDialogOpen(false)}
            disabled={downloadingConversationPdf}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={
              downloadingConversationPdf
                ? <CircularProgress size={16} color="inherit" />
                : <DownloadIcon />
            }
            onClick={handleExportConversationPdf}
            disabled={
              downloadingConversationPdf ||
              (!includeSummaryInPdf && !includeTranscriptInPdf)
            }
          >
            {downloadingConversationPdf ? 'Generando...' : 'Exportar PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationDetail;
